"use client";

import React, { useState, useEffect } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight, BrainCircuit, Network } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";

// Static AI-themed background component
const AIBackground = () => {
  return <div className="absolute inset-0 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-primary/10" />
      <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern id="ai-grid" width="40" height="40" patternUnits="userSpaceOnUse">
            <circle cx="20" cy="20" r="1" fill="currentColor" className="text-primary/20" />
          </pattern>
          <linearGradient id="ai-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="rgb(190, 95, 50)" stopOpacity="0.1" />
            <stop offset="100%" stopColor="rgb(190, 95, 50)" stopOpacity="0.3" />
          </linearGradient>
        </defs>
        <rect width="100%" height="100%" fill="url(#ai-grid)" />
        <g className="animate-pulse">
          <line x1="10%" y1="20%" x2="30%" y2="40%" stroke="url(#ai-gradient)" strokeWidth="1" />
          <line x1="30%" y1="40%" x2="50%" y2="30%" stroke="url(#ai-gradient)" strokeWidth="1" />
          <line x1="50%" y1="30%" x2="70%" y2="50%" stroke="url(#ai-gradient)" strokeWidth="1" />
          <line x1="70%" y1="50%" x2="90%" y2="35%" stroke="url(#ai-gradient)" strokeWidth="1" />
          <line x1="20%" y1="60%" x2="40%" y2="70%" stroke="url(#ai-gradient)" strokeWidth="1" />
          <line x1="40%" y1="70%" x2="60%" y2="65%" stroke="url(#ai-gradient)" strokeWidth="1" />
          <line x1="60%" y1="65%" x2="80%" y2="75%" stroke="url(#ai-gradient)" strokeWidth="1" />
        </g>
      </svg>
      <style>{`
        @keyframes float-ai {
          0%, 100% { transform: translateY(0px) translateX(0px); }
          33% { transform: translateY(-10px) translateX(5px); }
          66% { transform: translateY(5px) translateX(-5px); }
        }
      `}</style>
      <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-primary/10 rounded-full blur-3xl animate-pulse" style={{
      animation: 'float-ai 8s ease-in-out infinite'
    }} />
      <div className="absolute bottom-1/4 right-1/4 w-40 h-40 bg-primary/5 rounded-full blur-3xl" style={{
      animation: 'float-ai 10s ease-in-out infinite 2s'
    }} />
    </div>;
};

// Static Cloud-themed background component
const CloudBackground = () => {
  return <div className="absolute inset-0 overflow-hidden bg-gradient-to-br from-blue-950/30 via-slate-900/20 to-purple-950/30">
      <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <radialGradient id="cloud-glow">
            <stop offset="0%" stopColor="rgb(100, 150, 255)" stopOpacity="0.3" />
            <stop offset="100%" stopColor="rgb(100, 150, 255)" stopOpacity="0" />
          </radialGradient>
          <filter id="glow">
            <feGaussianBlur stdDeviation="3" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        
        {/* Network nodes */}
        <g className="animate-pulse" style={{
        animationDuration: '3s'
      }}>
          <circle cx="15%" cy="20%" r="3" fill="rgb(200, 200, 255)" opacity="0.6" filter="url(#glow)" />
          <circle cx="25%" cy="35%" r="4" fill="rgb(200, 200, 255)" opacity="0.7" filter="url(#glow)" />
          <circle cx="40%" cy="25%" r="3" fill="rgb(200, 200, 255)" opacity="0.5" filter="url(#glow)" />
          <circle cx="55%" cy="40%" r="5" fill="rgb(200, 200, 255)" opacity="0.8" filter="url(#glow)" />
          <circle cx="70%" cy="30%" r="3" fill="rgb(200, 200, 255)" opacity="0.6" filter="url(#glow)" />
          <circle cx="85%" cy="45%" r="4" fill="rgb(200, 200, 255)" opacity="0.7" filter="url(#glow)" />
          <circle cx="30%" cy="60%" r="3" fill="rgb(200, 200, 255)" opacity="0.5" filter="url(#glow)" />
          <circle cx="50%" cy="70%" r="4" fill="rgb(200, 200, 255)" opacity="0.6" filter="url(#glow)" />
          <circle cx="75%" cy="65%" r="3" fill="rgb(200, 200, 255)" opacity="0.7" filter="url(#glow)" />
        </g>
        
        {/* Connection lines */}
        <g stroke="rgb(100, 150, 255)" strokeWidth="1" opacity="0.3">
          <line x1="15%" y1="20%" x2="25%" y2="35%" className="animate-pulse" style={{
          animationDuration: '2s'
        }} />
          <line x1="25%" y1="35%" x2="40%" y2="25%" className="animate-pulse" style={{
          animationDuration: '2.5s'
        }} />
          <line x1="40%" y1="25%" x2="55%" y2="40%" className="animate-pulse" style={{
          animationDuration: '3s'
        }} />
          <line x1="55%" y1="40%" x2="70%" y2="30%" className="animate-pulse" style={{
          animationDuration: '2.2s'
        }} />
          <line x1="70%" y1="30%" x2="85%" y2="45%" className="animate-pulse" style={{
          animationDuration: '2.8s'
        }} />
          <line x1="25%" y1="35%" x2="30%" y2="60%" className="animate-pulse" style={{
          animationDuration: '3.2s'
        }} />
          <line x1="30%" y1="60%" x2="50%" y2="70%" className="animate-pulse" style={{
          animationDuration: '2.6s'
        }} />
          <line x1="50%" y1="70%" x2="75%" y2="65%" className="animate-pulse" style={{
          animationDuration: '2.9s'
        }} />
          <line x1="55%" y1="40%" x2="75%" y2="65%" className="animate-pulse" style={{
          animationDuration: '3.1s'
        }} />
        </g>
      </svg>
      
      {/* Floating cloud-like glows */}
      <div className="absolute top-1/3 left-1/5 w-48 h-48 bg-blue-500/10 rounded-full blur-3xl animate-pulse" style={{
      animationDuration: '4s'
    }} />
      <div className="absolute bottom-1/3 right-1/5 w-56 h-56 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{
      animationDuration: '5s',
      animationDelay: '1s'
    }} />
      <div className="absolute top-1/2 left-1/2 w-40 h-40 bg-cyan-500/10 rounded-full blur-3xl animate-pulse" style={{
      animationDuration: '6s',
      animationDelay: '2s'
    }} />
    </div>;
};
export default function TransformationPillars() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll();
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
  }, []);
  const pillars = [{
    id: "ai-consulting",
    title: "IT Consulting",
    icon: <BrainCircuit className="w-12 h-12 text-primary mb-4" />,
    description: "Inara Technologies delivers strategic IT consultancy to align technology with business goals, improve efficiency, reduce risk, and support long-term growth.",
    BackgroundComponent: AIBackground,
    link: "/SolutionsPage",
    colorClass: "text-primary"
  }, {
    id: "cloud-infra",
    title: "Cloud Infrastructure",
    icon: <Network className="w-12 h-12 text-accent mb-4" />,
    description: "Scalable, secure, and resilient cloud architectures for the modern enterprise.",
    BackgroundComponent: CloudBackground,
    link: "/SolutionsPage",
    colorClass: "text-accent"
  }];
  return <section id="transformation-pillars" ref={sectionRef} className={`dark bg-background text-foreground py-24 px-4 md:px-8 overflow-hidden transition-opacity duration-1000 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-4">
            Transformation Pillars
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
            Driving digital evolution through our core technical competencies.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 h-auto lg:h-[600px]">
          {pillars.map((pillar, index) => {
          const BackgroundComponent = pillar.BackgroundComponent;
          return <Link key={pillar.id} to={pillar.link} className="group block h-full w-full relative" data-index={index}>
                <Card className="h-[500px] lg:h-full w-full overflow-hidden relative border-border/50 bg-card/50 hover:border-primary/50 transition-all duration-500 cursor-pointer group-hover:shadow-2xl group-hover:shadow-primary/10">
                  {/* Background visualization */}
                  <div className="absolute inset-0 z-0 opacity-40 group-hover:opacity-60 transition-opacity duration-500">
                    {isMounted && <div className="w-full h-full">
                        <BackgroundComponent />
                      </div>}
                  </div>
                  
                  {/* Gradient overlay */}
                  <div className="absolute inset-0 z-10 bg-gradient-to-t from-background via-background/80 to-transparent pointer-events-none" />

                  {/* Content */}
                  <CardContent className="relative z-20 h-full flex flex-col items-center justify-center text-center p-8 md:p-12">
                    <div className="transform group-hover:-translate-y-4 transition-transform duration-500 flex flex-col items-center">
                      <div className={`p-4 rounded-full bg-background/50 backdrop-blur-sm border border-white/10 mb-6 shadow-lg ${pillar.colorClass}`}>
                        {pillar.icon}
                      </div>
                      
                      <h3 className="text-[2.5rem] font-bold leading-tight mb-4 tracking-tight">
                        {pillar.title}
                      </h3>
                      
                      <p className="text-muted-foreground text-lg max-w-md mb-8 opacity-80 group-hover:opacity-100 transition-opacity">
                        {pillar.description}
                      </p>

                      <div className="opacity-0 translate-y-4 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500 ease-out">
                        <Button size="lg" className="font-semibold text-base px-8">
                          View Services
                          <ArrowRight className="ml-2 w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>;
        })}
        </div>
      </div>
    </section>;
}